import { Component, OnInit, ViewChild, AfterViewInit, ElementRef, EventEmitter, Output, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { GlobalSearchService } from '../../../services/globalsearchservice.service'
import { PrintService } from '../../../services/print.service'
import { VendorService } from '../../../services/vendor.service'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { merge } from 'rxjs';
import { take } from 'rxjs/operators';
import { VendorPaymentModalComponent } from '../../vendor-payment-modal/vendor-payment-modal.component';
import { buildDashboardThemeVars } from '../../../shared/dashboard-widget-theme';
import { DashboardStatBucket } from '../../../shared/dashboard-stat-card/dashboard-stat-card.component';
import { trigger, state, style, transition, animate , query, group, stagger } from '@angular/animations';

@Component({
	selector: 'app-vendordash-overdue',
	templateUrl: './vendordash-overdue.component.html',
	styleUrls: ['./vendordash-overdue.component.scss'],
	animations: [
	  trigger('flipState', [
		  state('active', style({
			  transform: 'rotate3d(0, 1, 0, 179deg)',
		  })),
		  state('inactive', style({
			  transform: 'rotateY(0)'
		  })),
		  transition('active => inactive', animate('500ms ease-out')),
		  transition('inactive => active', animate('500ms ease-in')),
	  ]),
	]
})
export class VendordashOverdueComponent implements OnInit, OnChanges {

	flip = 'inactive';
	@Output() viewData = new EventEmitter<any>();
	/** Fired after a payment posts so an external data owner (as-of wrapper) can re-fetch. */
	@Output() refreshRequested = new EventEmitter<void>();

	/** When false, aged rows come from @Input() agedData (parent fetches dashboard). */
	@Input() loadAgedInternally = true;
	@Input() agedData: any = null;

	aged: any = null;
	aged30: any = [];
	aged60: any = [];
	aged90: any = [];
	original_aged: any = [];

	total_overdue_details = {
		thirty: 0,
		sixty: 0,
		ninety: 0,
		total: 0,
	}

	viewing: any = false;
	title: any = '';
	sending = false;
	pdf = false;
	paginator_show = true;
	@ViewChild('print_table') printtable: ElementRef;

	/** Row expanded to show its open AP transactions (same idiom as brand disbursements). */
	expandedRow: any = null;
	detailRows: any[] = [];
	detailLoading = false;

	searchTerm = '';
	sortField = 'due';
	sortDir: 'asc' | 'desc' = 'desc';

	private readonly numericFields = ['balance', 'due', 'overdue', 'overdue1', 'overdue2'];

	/** Rows actually rendered: the viewed bucket, filtered by search and sorted. */
	get displayRows(): any[] {
		let data: any[] = Array.isArray(this.viewing) ? this.viewing : [];

		const term = this.searchTerm.trim().toLowerCase();
		if (term) {
			data = data.filter((r) =>
				(r.suppname || '').toLowerCase().includes(term) ||
				String(r.supplierid || '').toLowerCase().includes(term));
		}

		const field = this.sortField;
		const dir = this.sortDir;
		const numeric = this.numericFields.includes(field);
		return [...data].sort((a, b) => {
			let av: any = a[field];
			let bv: any = b[field];
			if (numeric) {
				av = this.toNum(av);
				bv = this.toNum(bv);
			} else {
				av = (av || '').toString().toLowerCase();
				bv = (bv || '').toString().toLowerCase();
			}
			if (av < bv) { return dir === 'asc' ? -1 : 1; }
			if (av > bv) { return dir === 'asc' ? 1 : -1; }
			return 0;
		});
	}

	sortBy(field: string): void {
		if (this.sortField === field) {
			this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
		} else {
			this.sortField = field;
			this.sortDir = this.numericFields.includes(field) ? 'desc' : 'asc';
		}
		this.expandedRow = null;
	}

	getSortIcon(field: string): string {
		if (this.sortField !== field) {
			return 'unfold_more';
		}
		return this.sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward';
	}

	get themeVars(): Record<string, string> {
		return buildDashboardThemeVars('cw', 'info');
	}

	private _overdueBucketsCache: { key: string; value: DashboardStatBucket[] } = { key: '', value: [] };
	get overdueBuckets(): DashboardStatBucket[] {
		const d = this.total_overdue_details;
		const key = `${d.thirty}:${d.sixty}:${d.ninety}`;
		if (this._overdueBucketsCache.key !== key) {
			this._overdueBucketsCache = {
				key,
				value: [
					{ label: '30d', count: d.thirty, data: this.aged30, key: '30 Days' },
					{ label: '60d', count: d.sixty, data: this.aged60, key: '60 Days' },
					{ label: '90d', count: d.ninety, data: this.aged90, key: '90 Days' },
				],
			};
		}
		return this._overdueBucketsCache.value;
	}

	onOverdueBucket(bucket: DashboardStatBucket): void {
		this.toggleFlip(bucket.data, bucket.key || bucket.label);
	}

	constructor(private globalSearchService: GlobalSearchService, private printService: PrintService, private vendorService: VendorService, private modalService: NgbModal) {

	}

	/**
	 * Same streamlined pay flow as Brand Payouts Pending, in generic mode:
	 * no payoutRow, amount pre-filled with the vendor's past-due total, and
	 * every open invoice eligible for the oldest-first roll.
	 */
	openPaymentModal(row: any): void {
		const ref = this.modalService.open(VendorPaymentModalComponent, {
			size: 'xl',
			scrollable: true,
			windowClass: 'vendor-payment-modal-window',
		});
		ref.componentInstance.supplierid = String(row.supplierid);
		ref.componentInstance.vendorTitle = `${row.suppname} · ${row.supplierid}`;
		ref.componentInstance.vendorName = row.suppname || '';
		ref.componentInstance.suggestedAmount = this.toNum(row.due);

		merge(ref.closed, ref.dismissed)
			.pipe(take(1))
			.subscribe(() => this.reloadAged());
	}

	/** Expand a vendor row to its open AP transactions (invoices + credits). */
	toggleDetails(row: any): void {
		if (this.expandedRow === row) {
			this.expandedRow = null;
			this.detailRows = [];
			return;
		}
		this.expandedRow = row;
		this.loadDetails(row);
	}

	private loadDetails(row: any): void {
		this.detailRows = [];
		this.detailLoading = true;

		const today = new Date();
		const fiveYearsAgo = new Date();
		fiveYearsAgo.setFullYear(today.getFullYear() - 5);
		this.vendorService.getVendorTransactions({
			supplierid: row.supplierid,
			opentrans: true,
			types: [20, 21],
			fromdate: fiveYearsAgo,
			todate: today,
			keywords: '',
		}).subscribe((res: any) => {
			const list = res?.transactions ?? res ?? [];
			this.detailRows = Array.isArray(list) ? list : [];
			this.detailLoading = false;
		}, () => {
			this.detailLoading = false;
		});
	}

	/** Re-pull aged data after a payment: self-fetch, or ask the parent that owns the data. */
	private reloadAged(): void {
		if (this.loadAgedInternally) {
			this.vendorService.getDashBoard().subscribe((results: any) => {
				if (results) {
					this.applyAgedRows(results.aged || []);
				}
			});
		} else {
			this.refreshRequested.emit();
		}
	}

	toNum(v: any): number {
		if (v == null || v === '') {
			return 0;
		}
		if (typeof v === 'number') {
			return v;
		}
		const n = parseFloat(String(v).replace(/[^\d.-]/g, ''));
		return isNaN(n) ? 0 : n;
	}

	ngOnInit(): void {
		if (this.loadAgedInternally) {
			this.vendorService.getDashBoard().subscribe((results: any) => {
				if (results) {
					this.applyAgedRows(results.aged || []);
				}
			});
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (!this.loadAgedInternally && changes['agedData']) {
			if (this.agedData == null) {
				this.aged = null;
				this.aged30 = [];
				this.aged60 = [];
				this.aged90 = [];
				this.original_aged = [];
				this.total_overdue_details = { thirty: 0, sixty: 0, ninety: 0, total: 0 };
				return;
			}
			this.applyAgedRows(this.agedData);
		}
	}

	private applyAgedRows(rows: any): void {
		this.aged = Array.isArray(rows) ? rows : [];
		this.original_aged = this.aged;

		// SQL buckets are cumulative: overdue1 ⊆ due, overdue2 ⊆ overdue1.
		// Exclusive display ranges:
		//   30d = 0–29 days past due  → due > 0 AND overdue1 = 0
		//   60d = 30–59 days past due → overdue1 > 0 AND overdue2 = 0
		//   90d = 60+ days past due   → overdue2 > 0
		this.aged30 = this.aged.filter((v: any) => {
			return parseFloat(v.due) > 0.0000 && parseFloat(v.overdue1) <= 0.0000;
		});

		this.aged60 = this.aged.filter((v: any) => {
			return parseFloat(v.overdue1) > 0.0000 && parseFloat(v.overdue2) <= 0.0000;
		});

		this.aged90 = this.aged.filter((v: any) => {
			return parseFloat(v.overdue2) > 0.0000;
		});

		if (Array.isArray(this.aged)) {
			this.total_overdue_details.thirty = this.aged.reduce((acc: number, v: any) => {
				return acc + (parseFloat(v.due) > 0.0000 && parseFloat(v.overdue1) <= 0.0000 ? 1 : 0);
			}, 0);

			this.total_overdue_details.sixty = this.aged.reduce((acc: number, v: any) => {
				return acc + (parseFloat(v.overdue1) > 0.0000 && parseFloat(v.overdue2) <= 0.0000 ? 1 : 0);
			}, 0);

			this.total_overdue_details.ninety = this.aged.reduce((acc: number, v: any) => {
				return acc + (parseFloat(v.overdue2) > 0.0000 ? 1 : 0);
			}, 0);
		}

		this.total_overdue_details.total = this.total_overdue_details.thirty + this.total_overdue_details.sixty + this.total_overdue_details.ninety;

		// If the card is flipped open, re-point the viewed rows at the fresh bucket
		// (they're new array instances) so a posted payment shows up immediately.
		if (this.viewing) {
			switch (this.title) {
			case '30 Days': this.viewing = this.aged30; break;
			case '60 Days': this.viewing = this.aged60; break;
			case '90 Days': this.viewing = this.aged90; break;
			default: this.viewing = this.original_aged;
			}
		}

		// Re-point (or drop) the expanded row too — a paid-off vendor falls off the
		// list; a still-owed one keeps its panel open with freshly loaded details.
		if (this.expandedRow) {
			const fresh = (this.aged || []).find((r: any) =>
				String(r.supplierid) === String(this.expandedRow.supplierid));
			this.expandedRow = fresh || null;
			if (fresh) {
				this.loadDetails(fresh);
			} else {
				this.detailRows = [];
			}
		}
	}

	toggleFlip(data: any, title: string) {
		if (!data) {
			this.flip = 'inactive';
		} else {
			this.flip = (this.flip == 'active') ? 'inactive' : 'active';
		}

		if (this.flip == 'active') {
			this.viewing = data;
			this.title = title;
			const dashboard_data = { data: data, title: 'Over Due Vendors' };
			this.viewData.emit(dashboard_data);
		} else {
			this.viewing = false;
			this.viewData.emit(false);
		}
	}

	/** Table HTML for export with the on-screen-only pay/expand cells and detail panels removed. */
	private printableHtml(): string {
		const clone = this.printtable.nativeElement.cloneNode(true) as HTMLElement;
		clone.querySelectorAll('.vd-pay-cell, .vd-detail-row').forEach((el) => el.remove());
		return clone.innerHTML;
	}

	exportPdf() {
		this.sending = true;
		this.paginator_show = false;
		const encoded: string = this.globalSearchService.base64encode(this.printableHtml());
		const today = new Date();
		const data = {
			content: encoded,
			filename: 'overdue-vendors.pdf',
			landscape: true,
			title: 'Over Due Vendors',
			subtitle: 'Created ' + today.toLocaleString()
		};
		this.printService.pdf(data).subscribe((result: any) => {
			this.globalSearchService.downloadPdf(result.content, data.filename);
			this.sending = false;
			this.paginator_show = true;
		});
	}

	exportXls() {
		this.sending = true;
		const encoded: string = this.globalSearchService.base64encode(this.printableHtml());
		const today = new Date();
		const data = {
			content: encoded,
			filename: 'overdue-vendors.xlsx',
			title: 'Over Due Vendors',
			subtitle: 'Created ' + today.toLocaleString()
		};
		this.printService.xls(data).subscribe((result: any) => {
			this.globalSearchService.downloadXls(result.content, data.filename);
			this.sending = false;
		});
	}

}
